import { useId } from 'react'
import { EYE_RADIUS, POSE_HEIGHT } from './terrierArt'
import type { TerrierPose } from './terrierArt'

/** A border terrier's tan coat, with the grey-brown saddle over its back. */
const COAT = '#d9a05b'
const SADDLE = '#8a7364'
const MUZZLE = '#6d5f57'
const INK = '#7a4a12'
const NOSE = '#3b3430'
/** The retriever's eye, so the pair of them look like the same illustration. */
const EYE = '#4a2f14'
const EYE_GLINT = '#fbf1de'

interface TerrierProps {
  pose: TerrierPose
  /** Height on the page. The width follows from the pose. */
  heightPx: number
  className?: string
}

/**
 * The border terrier, in one of his poses.
 *
 * Simpler than the retriever next door: he is drawn from whole poses rather than
 * one drawing clipped into moving parts, so sitting down is a swap of path data
 * instead of a rig of rotations. The poses share a coordinate space with the
 * ground along the bottom edge, so swapping never changes his size or lifts him
 * off the floor.
 *
 * Order matters. The saddle is clipped to the body, or the blob would spill past
 * his back; the ink goes on last so the linework sits over every colour; and the
 * eyes go on after the ink, because they are ours rather than the drawing's.
 */
export function Terrier({ pose, heightPx, className }: TerrierProps) {
  const clip = `${useId()}-body`
  const scale = heightPx / POSE_HEIGHT

  return (
    <svg
      viewBox={`0 0 ${pose.width} ${POSE_HEIGHT}`}
      width={pose.width * scale}
      height={heightPx}
      className={className}
      aria-hidden="true"
    >
      <defs>
        <clipPath id={clip}>
          <path d={pose.body} />
        </clipPath>
      </defs>

      <path d={pose.body} fill={COAT} />
      {pose.saddle !== null && (
        <g clipPath={`url(#${clip})`}>
          <path d={pose.saddle} fill={SADDLE} />
        </g>
      )}
      <path d={pose.muzzle} fill={MUZZLE} />
      {/* The drawing's own nose ring, filled in. Under the ink, so its outline
          stays drawn round the outside of it. */}
      <path d={pose.nose} fill={NOSE} />
      <path d={pose.ink} fill={INK} />

      {pose.eyes.map((eye) => (
        <g key={`${eye.cx},${eye.cy}`}>
          <circle cx={eye.cx} cy={eye.cy} r={EYE_RADIUS} fill={EYE} />
          <circle
            cx={eye.cx + EYE_RADIUS * 0.36}
            cy={eye.cy - EYE_RADIUS * 0.41}
            r={EYE_RADIUS * 0.34}
            fill={EYE_GLINT}
          />
        </g>
      ))}
    </svg>
  )
}
